import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { EditingCell } from './useEditing'
import type { GridApi, GridColumn, GridRowDrag } from './types'
import type { GridLeafColumn } from './useGridState'
import type { GridTreeMeta } from './useTreeRows'
import GridRow from './GridRow'
import SortableGridRow from './rowDrag'
import GridSkeletonRow from './GridSkeletonRow'
import { loadingRowId } from './useServerSideRows'
import classes from './grid-theme.module.scss'

interface GridBodyProps<T> {
  rows: T[]
  getRowId: (row: T) => string
  leafColumns: GridLeafColumn<T>[]
  /** Width of the virtualized-away columns before / after the rendered slice. */
  leadSpacer?: number
  trailSpacer?: number
  api: GridApi<T>
  rowHeight: number
  autoHeight: boolean
  totalWidth: number
  editingCell: EditingCell | null
  focusedCell?: { rowId: string; field: string } | null
  pendingEdits: Map<string, Partial<T>>
  fillHandleCell?: { rowId: string; field: string } | null
  /** Rows the in-progress drag currently covers, for the preview outline. */
  fillPreview?: { field: string; from: number; to: number } | null
  onFillHandlePointerDown?: (event: React.PointerEvent, field: string) => void
  onFillHandleDoubleClick?: (event: React.MouseEvent, field: string) => void
  rowClass?: (ctx: { row: T; rowId: string; rowIndex: number }) => string | undefined
  onCellClick: (event: React.MouseEvent, rowId: string, field: string, editable: boolean) => void
  onCommit: (
    rowId: string,
    column: GridColumn<T>,
    row: T,
    value: unknown,
    baseline: unknown,
  ) => void
  onCancelEdit: () => void
  onKeyDown?: (event: React.KeyboardEvent, rowId: string, field: string, row: T) => void
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
  onReachEnd?: () => void
  viewportRef: React.MutableRefObject<HTMLDivElement | null>
  /** Server-side model: rows whose block has not arrived draw as skeletons. */
  isRowLoading?: (row: T) => boolean
  /** Reports the rendered window so the server model can fetch its blocks. */
  onVisibleRangeChange?: (startIndex: number, endIndex: number) => void
  /** Tree row model: per-row depth and hierarchy, keyed by row id. */
  treeMetaById?: Map<string, GridTreeMeta>
  rowDrag?: GridRowDrag<T>
}

/**
 * Rows kept mounted beyond each edge. Every extra row is a full set of cells
 * to build, so this stays small: enough to cover a fast wheel tick, not enough
 * to double the work.
 */
const OVERSCAN = 4
/** A pointer has to travel this far before a drag starts, so clicks still land. */
const DRAG_ACTIVATION_DISTANCE_PX = 4
/** Load-more fires this far from the bottom, so the fetch overlaps the scroll. */
const REACH_END_THRESHOLD_PX = 200

const GridBody = <T,>({
  rows,
  getRowId,
  leafColumns,
  leadSpacer,
  trailSpacer,
  api,
  rowHeight,
  autoHeight,
  totalWidth,
  editingCell,
  focusedCell,
  pendingEdits,
  fillHandleCell,
  fillPreview,
  onFillHandlePointerDown,
  onFillHandleDoubleClick,
  rowClass,
  onCellClick,
  onCommit,
  onCancelEdit,
  onKeyDown,
  onScroll,
  onReachEnd,
  viewportRef,
  isRowLoading,
  onVisibleRangeChange,
  treeMetaById,
  rowDrag,
}: GridBodyProps<T>) => {
  const reachedEndRef = useRef(false)
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null)

  // Measured heights are cached per item key, and the default key is the row's
  // index. A re-sort then leaves every measurement filed under whichever row
  // used to sit there — a three-line error row's height applied to the plain
  // row that took its place — and the offsets built from them overlap the rows
  // on screen. Keying by row id makes a measurement follow its own row.
  // Placeholder rows have no id of their own, so they take the same synthetic
  // id `renderRow` gives them.
  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index]
      if (!row) return loadingRowId(index)
      return isRowLoading?.(row) ? loadingRowId(index) : getRowId(row)
    },
    [rows, getRowId, isRowLoading],
  )

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => rowHeight,
    getItemKey,
    overscan: OVERSCAN,
    // Dynamic measurement: wrapText rows and multi-line status cells are
    // taller than the estimate. Prefer the observer's border-box size over
    // getBoundingClientRect: a page that mounts the grid inside a scale
    // transition (config tabs, Station Setup cards) reports a shrunken rect
    // mid-animation, and since the border-box never changes the observer
    // would never correct it — every row stayed stuck at its animated size.
    measureElement: (el, entry) => {
      const boxHeight = (entry?.borderBoxSize?.[0] as ResizeObserverSize | undefined)
        ?.blockSize
      if (boxHeight != null && boxHeight > 0) return boxHeight
      return (el as HTMLElement | undefined)?.offsetHeight || rowHeight
    },
    enabled: !autoHeight,
  })

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      onScroll?.(event)
      if (!onReachEnd) return

      const el = event.currentTarget
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight
      if (distance < REACH_END_THRESHOLD_PX) {
        if (!reachedEndRef.current) {
          reachedEndRef.current = true
          onReachEnd()
        }
      } else {
        reachedEndRef.current = false
      }
    },
    [onScroll, onReachEnd],
  )

  // A shorter list after a filter change must be able to fire load-more again.
  useEffect(() => {
    reachedEndRef.current = false
  }, [rows.length])

  const virtualItems = virtualizer.getVirtualItems()
  const firstVisible = virtualItems[0]?.index ?? 0
  const lastVisible = virtualItems[virtualItems.length - 1]?.index ?? 0

  // Reported from an effect rather than the scroll handler: the virtualizer
  // decides the window, and a jump straight to row 4,000 (a scrollbar drag)
  // never fires the gradual scroll a handler would react to.
  useEffect(() => {
    if (!onVisibleRangeChange || virtualItems.length === 0) return
    onVisibleRangeChange(firstVisible, lastVisible)
  }, [onVisibleRangeChange, firstVisible, lastVisible, virtualItems.length])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
  )

  const sortableIds = useMemo(
    () => (rowDrag?.enabled ? rows.map((row) => getRowId(row)) : []),
    [rowDrag?.enabled, rows, getRowId],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingRowId(null)
      if (!rowDrag) return

      const fromIndex = rows.findIndex((row) => getRowId(row) === event.active.id)
      const toIndex = rows.findIndex((row) => getRowId(row) === event.over?.id)
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return

      const row = rows[fromIndex]
      const targetRow = rows[toIndex]

      // A tree row may only move among its own siblings: dropping it under a
      // different parent would reparent it, which reordering does not mean.
      if (treeMetaById) {
        const fromParent = treeMetaById.get(getRowId(row))?.parentId ?? null
        const toParent = treeMetaById.get(getRowId(targetRow))?.parentId ?? null
        if (fromParent !== toParent) return
      }

      if (rowDrag.canDrop && !rowDrag.canDrop({ row, targetRow })) return

      rowDrag.onReorder({ row, fromIndex, toIndex })
    },
    [rowDrag, rows, getRowId, treeMetaById],
  )

  /** Which edge of the fill range a row sits on — see `fillPreviewEdge`. */
  const fillPreviewEdgeAt = (
    index: number,
  ): 'single' | 'top' | 'bottom' | 'middle' | null => {
    if (!fillPreview) return null
    const first = Math.min(fillPreview.from, fillPreview.to)
    const last = Math.max(fillPreview.from, fillPreview.to)
    if (index === first && index === last) return 'single'
    if (index === first) return 'top'
    if (index === last) return 'bottom'
    return 'middle'
  }

  const renderRow = (
    row: T,
    index: number,
    virtual: boolean,
    // `offsetY` is the virtualizer's; an autoHeight grid has none and passes
    // the height alone.
    offset?: { offsetY?: number; minHeight: number },
    measureRef?: (el: HTMLElement | null) => void,
  ) => {
    const style = offset
      ? offset.offsetY == null
        ? { minHeight: offset.minHeight }
        : {
            transform: `translateY(${offset.offsetY}px)`,
            minHeight: offset.minHeight,
          }
      : undefined

    if (isRowLoading?.(row)) {
      return (
        <GridSkeletonRow
          key={loadingRowId(index)}
          rowId={loadingRowId(index)}
          rowIndex={index}
          leafColumns={leafColumns}
          leadSpacer={leadSpacer}
          trailSpacer={trailSpacer}
          style={style}
        />
      )
    }

    const rowId = getRowId(row)
    const rowProps = {
      row,
      rowId,
      rowIndex: index,
      leafColumns,
      leadSpacer,
      trailSpacer,
      api,
      isLast: index === rows.length - 1,
      depth: treeMetaById?.get(rowId)?.depth ?? 0,
      editingField: editingCell?.rowId === rowId ? editingCell.field : null,
      editorOpenList: Boolean(editingCell?.openList),
      focusedField: focusedCell?.rowId === rowId ? focusedCell.field : null,
      pendingEdit: pendingEdits.get(rowId) ?? null,
      fillHandleField:
        fillHandleCell?.rowId === rowId ? fillHandleCell.field : null,
      fillPreviewField:
        fillPreview &&
        index >= Math.min(fillPreview.from, fillPreview.to) &&
        index <= Math.max(fillPreview.from, fillPreview.to)
          ? fillPreview.field
          : null,
      // Which edge of the range this row is, so the run is outlined as one
      // block rather than as a stack of separately ringed cells.
      fillPreviewEdge: fillPreviewEdgeAt(index),
      onFillHandlePointerDown,
      onFillHandleDoubleClick,
      rowClassName: rowClass?.({ row, rowId, rowIndex: index }),
      offsetY: offset?.offsetY,
      minHeight: offset?.minHeight,
      virtual,
      onCellClick,
      onCommit,
      onCancelEdit,
      onKeyDown,
      measureRef,
    }

    if (rowDrag?.enabled) {
      return (
        <SortableGridRow
          key={rowId}
          {...rowProps}
          canDrag={rowDrag.canDrag ? rowDrag.canDrag(row) : true}
        />
      )
    }

    return <GridRow key={rowId} {...rowProps} />
  }

  // Only wraps the rows when the grid actually allows dragging: a DndContext
  // around every grid would add listeners (and a re-render on each pointer
  // move) to grids that have no use for them.
  const withDrag = (children: React.ReactNode) => {
    if (!rowDrag?.enabled) return children
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => setDraggingRowId(String(event.active.id))}
        onDragCancel={() => setDraggingRowId(null)}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      </DndContext>
    )
  }

  if (autoHeight) {
    return (
      <div
        ref={viewportRef}
        className={`${classes.body} ${classes.bodyAutoHeight}`}
        onScroll={handleScroll}
        role="rowgroup"
        data-dragging={draggingRowId ?? undefined}
      >
        <div style={{ width: totalWidth, minWidth: '100%' }}>
          {withDrag(
            rows.map((row, index) =>
              renderRow(row, index, false, { minHeight: rowHeight }),
            ),
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={viewportRef}
      className={classes.body}
      onScroll={handleScroll}
      role="rowgroup"
      data-dragging={draggingRowId ?? undefined}
    >
      <div
        className={classes.virtualSizer}
        style={{ height: virtualizer.getTotalSize(), width: totalWidth, minWidth: '100%' }}
      >
        {withDrag(
          virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index]
            if (!row) return null
            return renderRow(
              row,
              virtualRow.index,
              true,
              { offsetY: virtualRow.start, minHeight: rowHeight },
              virtualizer.measureElement,
            )
          }),
        )}
      </div>
    </div>
  )
}

export default GridBody
